import i18n from "@/i18n";
import {
  getAllUsersHttp,
  getAllUsersByTypeHttp,
  addUserHttp,
  updateUserHttp,
  updateUserProfileHttp,
  deleteUserHttp,
  fetchAllUsersDG2Http,
  getCountHttp,
  getAllUsersPaginationHttp,
  getHistoricHttp,
} from "@/services/planning/UserServices.js";
import store from "@/store/index.js";
import authService from "@/services/planning/AuthService";

const state = {
  users: [],
  calendarType: null, // use to empty filters when switch to a different calendar
  usersMultiArray: { locations: [], users: [], skills: [], companies: [] },
  totalItemCount: null,
};

const getters = {
  getUsers(state) {
    return state.users;
  },
  getFormateurs(state) {
    return state.users.filter(
      (u) => u.types.includes("FORMATEUR") && u.display === true
    );
  },
  getUsersByType: (state) => (type) => {
    return state.users.filter(
      (u) => u.types.includes(type) && u.display === true
    );
  },
  getUsersForm(state) {
    if (state.calendarType === "INTERVNOTASSIGN") {
      return state.users.filter((u) => u.types.includes("FORMATEUR"));
    } else {
      return state.users.filter((u) => u.types.includes(state.calendarType));
    }
  },
  getUsersExisting(state) {
    return state.users.filter((u) => !u.types.includes("INTERVNOTASSIGN"));
  },
  getNotAssignedUsers(state) {
    return state.users.filter((u) => u.types.includes("INTERVNOTASSIGN"));
  },
  getOnUserDisplay(state, getters, rootState, rootGetters) {
    // return state.users.filter(u => (u.display === true) && (u.type === state.calendarType));
    let users = [];
    const type = state.calendarType;
    let events = [];

    if (type.includes("_")) {
      type.split("_").forEach((t) => {
        users = users.concat(
          state.users.filter((u) => u.types.includes(t) && u.display === true)
        );
      });
    } else {
      users = state.users.filter(
        (u) => u.types.includes(type) && u.display === true
      );
    }

    if (type == "INDEPENDANT") {
      events = rootGetters["eventModule/getEvents"];

      let userIds = [];
      state.users.map((u) => {
        let userEvents = events.filter((e) => e.details.user.id == u.id);
        if (userEvents.length > 0) {
          userIds.push(u.id);
        }
      });
      users = state.users.filter(
        (u) =>
          u.types.includes(type) && u.display === true && userIds.includes(u.id)
      );
    }

    return users;
  },
  getUsersMultiArray(state) {
    return state.usersMultiArray;
  },
  getCalendar(state) {
    return state.calendarType;
  },
  getTotalItemCount(state) {
    return state.totalItemCount;
  },
  getOne: (state) => (idDg2) => {
    return state.users.find(
      (u) => u.details.idDg2 === idDg2 && u.display === true
    );
  },
  getUsersByLocationId: (state) => (locationId) => {
    return state.users.filter(
      (u) => u.details.locationId === locationId && u.display === true
    );
  },
  getUsersByCenter: (state) => (center) => {
    return getters.getUsersByLocationId(state)(center.locationId);
  },
  getUserById: (state) => (userId) => {
    return state.users.find((x) => x.id === userId);
  },
  getCurrentUser: (state) => {
    const userId = authService.getUser().id;
    return state.users.find((x) => x.id === userId);
  },
};

const mutations = {
  SET_USERS: (state, data) => {
    state.users = data;
  },
  ADD_NEW_USER: (state, payload) => state.users.push(payload),
  EDIT_USER: (state, payload) => {
    let index = state.users.findIndex((l) => l.id === payload.id);
    if (index !== -1) state.users.splice(index, 1, payload);
  },
  REMOVE_USER: (state, payload) => {
    let index = state.users.findIndex((l) => l.id === payload);
    if (index !== -1) state.users.splice(index, 1);
  },
  SET_USERS_MULTIARRAY: (state, { locations, users, skills, companies }) => {
    state.usersMultiArray.locations = locations;
    state.usersMultiArray.users = users;
    state.usersMultiArray.skills = skills;
    state.usersMultiArray.companies = companies;
  },
  SET_CALENDAR_TYPE: (state, data) => {
    state.calendarType = data;
  },
  SET_ITEM_COUNT: (state, data) => (state.totalItemCount = data),
  SET_HISTORIC_USER: (state, { userIdDg2, historic }) => {
    state.users.find((u) => u.details.idDg2 === userIdDg2).historic =
      historic.data;
  },
};

const actions = {
  /**
   * This method is setting the data timePeriod for the calendar
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {Number} userIdDg2 id of user
   */
  async setHistoricOnUser({ commit }, { userIdDg2 }) {
    let historic = await getHistoricHttp(userIdDg2);
    commit("SET_HISTORIC_USER", { userIdDg2, historic });
  },
  /**
   * This method will set the calendar type which is selected by the client (trainer, salesperson, admin,...)
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {String} calendarType
   */
  async fetchCalendarType({ commit }, calendarType) {
    commit("SET_CALENDAR_TYPE", calendarType);
  },
  /**
   * This method will update parameters selected for the user filter
   *
   * @param  {Vuex} {commit} necessary if call commit inside the method
   * @param  {} {locations filter users depending of the locations chosen
   * @param  {} users filter users depending of the users chosen
   * @param  {} skills filter users depending of the skills chosen
   * @param  {} companies} filter users depending of the companies chosen
   */
  async updateUsersMultiarray(
    { commit },
    { locations, users, skills, companies }
  ) {
    commit("SET_USERS_MULTIARRAY", { locations, users, skills, companies });
  },
  /**
   * This method will create the user object necessary for the proper functioning of the calendar in the Front-End.
   *
   * @param  {Vuex} {rootGetters} necessary if call getters from other stores
   * @param  {Object - userDto} userBack user received from the Rest API that we need to transform in order to be able to be used in the calendar
   * @returns {Object - user Front-End} return a user that can be used by the calendar in the Front-End
   */
  async createUserObject({ rootGetters }, userBack) {
    // { text: i18n.t("crudUser.hLastName"), value: "fullname", sortable: true },
    // { text: i18n.t("crudUser.hEmail"), value: "email", sortable: true },
    // { text: i18n.t("crudUser.hType"), value: "type", sortable: true },
    // { text: i18n.t("crudUser.hCompany"), value: "company", sortable: true },
    // { text: i18n.t("crudUser.hSkills"), value: "skills", sortable: true },

    let u = userBack;
    let location = rootGetters["locationModule/getLocations"].find(
      (l) => l.id === u.locationId
    );

    let user = {
      id: u.id,
      display: true,
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      type: u.type,
      types: u.types,
      company: u.company,
      skills: u.skills,
      color: location ? location.color : "#FFFFF",
      location: location ? location.city : "Ville masqué / Hidden city",
      details: u,
      fullname: u.fullName,
      employeeSlug: u.employeeSlug,
      personSlug: u.personSlug,
      historic: null,
    };

    if (user.details.imagePath === null) {
      if (user.details.company === "JEHANN")
        user.details.imagePath = require("@/assets/images/jehann_logo.png");
      else user.details.imagePath = require("@/assets/images/dawan_logo.png");
    }

    return user;
  },
  /**
   * This method will load all users from the database
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {} dispatch} necessary if call dispatch inside the method
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("SET_USERS", users)
   */
  async loadUsers({ commit, dispatch }) {
    await getAllUsersHttp()
      .then((response) => {
        const users = response.data.map((u) => setUserImage(u));

        let createdList = [];

        users.forEach((u) => {
          dispatch("createUserObject", u).then((u) => createdList.push(u));
        });

        commit("SET_USERS", createdList);
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userAllError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },

  /**
   * Loads all users and their dependencies from database
   */
  async loadUsersWithDependents(commit_dispatch) {
    const users = store.getters["userModule/getUsers"];
    if (!users || users.length === 0) {
      const locations = store.getters["locationModule/getLocations"];
      if (!locations || locations.length === 0)
        await store.dispatch("locationModule/loadLocations", commit_dispatch);

      await store.dispatch("userModule/loadUsers", commit_dispatch);
    }
  },

  /**
   * This method will load users depending of page, size and search parameters
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {} {page page number that will be load
   * @param  {} size number of users that will be load (represents LIMIT in sql)
   * @param  {} search} only users containing 'search' will be load
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("SET_USERS", users)
   */
  async loadUsersPagination({ commit, dispatch }, { page, size, search }) {
    await getAllUsersPaginationHttp(page, size, search)
      .then((response) => {
        const users = response.data.map((u) => setUserImage(u));
        commit("SET_USERS", users);
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userAllError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method will give the number of users containing 'search'
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {} {search} only users containing 'search' will be counted
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("SET_ITEM_COUNT", response.data.nb)
   */
  async getCount({ commit, dispatch }, { search }) {
    await getCountHttp(search)
      .then((response) => {
        commit("SET_ITEM_COUNT", response.data.nb);
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.courseAllError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method is calling the http call to add an user in the database and then in the Vuex store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - userDto} user object that we want to add in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("ADD_NEW_USER", response.data)
   */
  async addUser({ commit, dispatch }, user) {
    await addUserHttp(user)
      .then((response) => {
        commit("ADD_NEW_USER", response.data);
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userAddSuccess"),
            typeAlert: "success",
          },
          { root: true }
        );
      })
      .catch((error) => {
        if (error.response.data.length > 0) {
          error.response.data.forEach((error) => {
            for (const [key, value] of Object.entries(i18n.t("errorBackend"))) {
              if (error.type === key) {
                for (const [keyE, valueE] of Object.entries(value)) {
                  if (keyE === "message")
                    dispatch(
                      "notificationModule/showErrorMessages",
                      { valueE },
                      { root: true }
                    );
                }
              }
            }
          });
        }
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userAddError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method is calling the http call to edit an user in the database and then in the VueX store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - userDto} user object that we want to edit in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("EDIT_USER", response.data)
   */
  async editUser({ commit, dispatch }, user) {
    await updateUserHttp(user)
      .then((response) => {
        commit("EDIT_USER", response.data),
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.userEditSuccess"),
              typeAlert: "success",
            },
            { root: true }
          );
      })
      .catch((error) => {
        if (error.response.data.length > 0) {
          error.response.data.forEach((error) => {
            for (const [key, value] of Object.entries(i18n.t("errorBackend"))) {
              if (error.type === key) {
                for (const [keyE, valueE] of Object.entries(value)) {
                  if (keyE === "message")
                    dispatch(
                      "notificationModule/showErrorMessages",
                      { valueE },
                      { root: true }
                    );
                }
              }
            }
          });
        }
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userEditError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method is calling the http call to edit an user profile in the database and then in the VueX store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - editProfileDto} user object that we want to edit in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("EDIT_USER", response.data)
   */
  async editUserProfile({ commit, dispatch }, user) {
    await updateUserProfileHttp(user)
      .then((response) => {
        commit("EDIT_USER", response.data),
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.userEditSuccess"),
              typeAlert: "success",
            },
            { root: true }
          );
      })
      .catch((error) => {
        if (error.response.data.length > 0) {
          error.response.data.forEach((error) => {
            for (const [key, value] of Object.entries(i18n.t("errorBackend"))) {
              if (error.type === key) {
                for (const [keyE, valueE] of Object.entries(value)) {
                  if (keyE === "message")
                    dispatch(
                      "notificationModule/showErrorMessages",
                      { valueE },
                      { root: true }
                    );
                }
              }
            }
          });
        }
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userEditError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method is calling the http call to delete an user in the database and then in the VueX store
   *
   * @param  {Vuex} {commit necessary if call commit inside the method
   * @param  {Vuex} dispatch} necessary if call dispatch inside the method
   * @param  {Object - userDto} user object that we want to delete in the database
   * @returns {} it is returning nothing because the response will be load in the store directly => commit("REMOVE_USER", user.id)
   */
  async deleteUser({ commit, dispatch }, user) {
    await deleteUserHttp(user.id)
      .then((response) => {
        if (response.status === 202) {
          commit("REMOVE_USER", user.id),
            dispatch(
              "notificationModule/showNotification",
              {
                messageAlert: i18n.t("messageStores.userDeleteSuccess"),
                typeAlert: "success",
              },
              { root: true }
            );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userDeleteError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
  /**
   * This method is calling the http call to fetch users from the webservice Wdg2 (create or update depending if the course exists or not)
   *
   * @param  {Vuex} {dispatch} necessary if call dispatch inside the method
   * @param  {Object - LogInUser} logInUser user dg2
   * @returns {} it is returning nothing. The database is updated directly in the Back-End when the method is called.
   */
  async fetchUsersDG2({ dispatch }, { logInUser }) {
    await fetchAllUsersDG2Http(logInUser)
      .then((response) => {
        if (response.status === 200) {
          dispatch("loadUsers");
          dispatch(
            "notificationModule/showNotification",
            {
              messageAlert: i18n.t("messageStores.userAllSuccess"),
              typeAlert: "success",
            },
            { root: true }
          );
        }
      })
      .catch((error) => {
        dispatch(
          "notificationModule/showNotification",
          {
            messageAlert: i18n.t("messageStores.userAllError"),
            typeAlert: "error",
          },
          { root: true }
        );
      });
  },
};

/**
 * Set Default User Image IF none is Setted Up
 *
 * @param  {Object - userDto} user
 * @returns {Object - userDto}
 */
function setUserImage(user) {
  if (user.imagePath === null) {
    if (user.company === "JEHANN")
      user.imagePath = require("@/assets/images/jehann_logo.png");
    else user.imagePath = require("@/assets/images/dawan_logo.png");
  }

  return user;
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
